/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Box, HStack, Icon, IconButton, Input, InputGroup, InputRightElement, SimpleGrid, Stack, Text } from '@chakra-ui/core';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdSearch } from 'react-icons/md';
import { useStoreState } from '../../../../../models/hooks';
import { QUESTIONNAIRE_ELEMENT_TYPE } from '../../questionnaireTemplate.enums';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { formElementSchema } from '../schemas/formElement.schema';
import { FormElementItem } from './FormElementItem';
import { UnsavedChangesModal } from '../formBuilderComponents/UnsavedChangesModal';

const DRAG_THRESHOLD = 5;
interface PointerStartState {
  x: number;
  y: number;
  type: any;
}

export const FormBuilderElementList: React.FC = () => {
  const { t } = useTranslation('questionnaireTemplate');
  const startPos = useRef<PointerStartState | null>(null);
  const isDragging = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isFreeFormQuestionEnabled = useStoreState((s) => s.app.accounts?.configuration?.freeFormQuestionEnabled ?? false);
  const isVideoResponseEnabled = useStoreState((s) => s.app.accounts?.configuration?.showVideoQuestions ?? false);
  const isDocumentUploadEnabled = useStoreState((s) => s.app.accounts?.configuration?.showDocumentQuestions ?? false);

  const { handleDragStart, handleDragEnd } = useDragAndDrop();
  const addElement = QuestionnaireTemplateStore.useStoreActions((action) => action.addElementToRoot);

  const enabledElements = useMemo(() => {
    return Object.values(formElementSchema).filter((e) => {
      switch (e.questionType) {
        case QUESTIONNAIRE_ELEMENT_TYPE.FREE_FORM:
          return isFreeFormQuestionEnabled && e.enabled;

        case QUESTIONNAIRE_ELEMENT_TYPE.VIDEO_RESPONSE:
          return isFreeFormQuestionEnabled && isVideoResponseEnabled && e.enabled;

        case QUESTIONNAIRE_ELEMENT_TYPE.DOCUMENT_UPLOAD:
          return isFreeFormQuestionEnabled && isDocumentUploadEnabled && e.enabled;

        default:
          return e.enabled;
      }
    });
  }, [isFreeFormQuestionEnabled, isVideoResponseEnabled, isDocumentUploadEnabled]);

  const filteredElements = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return term
      ? enabledElements.filter((e) => t(`formBuilder.schema.${e.label}`).toLowerCase().includes(term))
      : enabledElements;
  }, [enabledElements, searchTerm, t]);

  const onPointerDown = useCallback((e: React.PointerEvent, type: string) => {
    if (e.button !== 0) return;

    startPos.current = { x: e.clientX, y: e.clientY, type };
    isDragging.current = false;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 && e.buttons !== 1) return;
      if (!startPos.current || isDragging.current) return;

      const deltaX = Math.abs(e.clientX - startPos.current.x);
      const deltaY = Math.abs(e.clientY - startPos.current.y);

      if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
        isDragging.current = true;
        handleDragStart(e, {
          type: startPos.current.type,
          isNew: true,
        });
      }
    },
    [handleDragStart],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!startPos.current) return;

      if (e.button !== 0) {
        startPos.current = null;
        isDragging.current = false;
        return;
      }

      if (!isDragging.current) {
        addElement(startPos.current.type);
      } else {
        handleDragEnd(e);
      }

      startPos.current = null;
      isDragging.current = false;
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    },
    [addElement, handleDragEnd],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      if (isDragging.current) {
        handleDragEnd(e);
      }
      startPos.current = null;
      isDragging.current = false;
    },
    [handleDragEnd],
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchTerm('');
      e.currentTarget.blur();
    }
  }, []);

  return (
    <Box p={6} bg="white" minH="100%" data-testid="FormBuilderElementList">
      <Stack spacing={6}>
        <HStack>
          <UnsavedChangesModal />
          <InputGroup>
            <Input
              placeholder={t('formBuilder.elementList.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              aria-label="Search form components"
              aria-describedby="search-help"
            />
            <InputRightElement>
              {searchTerm ? (
                <IconButton
                  aria-label="Clear search"
                  icon={<Text fontSize="lg">×</Text>}
                  size="xs"
                  variant="ghost"
                  onClick={handleClearSearch}
                  _hover={{ bg: 'gray.100' }}
                />
              ) : (
                <Icon as={MdSearch} color="gray.400" />
              )}
            </InputRightElement>
          </InputGroup>
        </HStack>

        <Text>{t('formBuilder.elementList.title')}</Text>

        <SimpleGrid columns={2} spacing={4} alignItems="stretch">
          {filteredElements.map(({ label, icon, questionType }) => (
            <FormElementItem
              key={questionType}
              label={label}
              icon={icon}
              type={questionType}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              data-testid={`form-element-${questionType}`}
            />
          ))}
        </SimpleGrid>
      </Stack>
    </Box>
  );
};
